import { ChangeDetectorRef, Directive, ElementRef, inject, Input, OnDestroy, OnInit, Renderer2, ViewRef } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Meta } from '@df/core/meta/meta';
import { NgInject } from '@df/ng/ng-inject';
import { IZippyEvent, ZippyAbstract, ZippyEventType } from '@df/legacy/ui/zippy/zippy.abstract';
import { Subscription } from 'rxjs';

const HIDDEN_CLASS = 'ng-hide';

/**
 * Marks contents of zippy
 *
 * ```html
 * <div zippy="some.name"></div>
 * ```
 *
 * @deprecated Please use ToggleService and ToggleClass instead
 */
@Directive({
    selector: '[zippy]',
    standalone: false
})
export class ZippyDirective extends ZippyAbstract implements OnDestroy, OnInit {
    protected _zippyOff?: boolean;

    /**
     * If state is locked and cannot be changed (tempporaty for router evenrts)
     * its a timoeut id, if set the state is locked
     */
    private stateLockedTimeout?: number;

    /**
     * Viewport changes subscription
     */
    protected subscriptionViewport?: Subscription;

    /**
     * Forced zippy state from the input (initially)
     * or change later with visiblity setter
     */
    protected _zippyState = false;

    /**
     * Set this flag for auto opening
     * It can be set on just selected zippies and first one will open
     * @type boolean
     */
    @Input() zippyAuto?: boolean;

    /**
     * Set this flag if you want zippy to close when navigating
     * @type boolean
     */
    @Input() zippyCloseOnNav?: boolean;

    /**
     * Set this flag if you want zippy to close when url change
     * @type boolean
     */
    @Input() zippyCloseOnRouterStart?: boolean;

    /**
     * If set then multiple instances are allowed
     * @type boolean
     */
    @Input() zippyMulti = false;

    @Input()
    zippyCloseOnEvent?: string;

    /**
     * Conditional directive apply.
     *
     * ```html
     * [zippyOff]="client.isS"
     * ```
     *
     * @memberof ZippyDirective
     */
    @Input() set zippyOff(value: boolean) {
        this._zippyOff = value;

        this.setVisibility(value);
    }

    /**
     * If zippy should open
     * @type boolean
     */
    @Input() set zippyState(to: boolean) {
        this.setVisibility(to);
    }

    /**
     * Respect zippy name attribute for name parsing.
     */
    @Input() set zippy(name: string | (string | number)[]) {
        this.parseName(name);
    }

    private meta = inject(Meta);
    protected router = inject(Router);

    constructor(
        protected ngEl: ElementRef,
        protected renderer2: Renderer2,
        protected changeDetectorRef: ChangeDetectorRef
    ) {
        super();
        this.timeoutService.async(() => {
            this.subscriptions.add(
                this.meta.routerEvents.subscribe(routerEvent => {
                    if (routerEvent instanceof NavigationStart && this.zippyCloseOnNav) {
                        this.onRouterEvent();
                    }
                })
            );
            this.subscriptions.add(
                this.router.events.subscribe(event => {
                    if (event instanceof NavigationStart && this.zippyCloseOnRouterStart) {
                        this.onRouterEvent();
                    }
                })
            );
        });
    }

    override ngOnInit(): void {
        super.ngOnInit();
        if (this.zippyCloseOnEvent) {
            this.timeoutService.async(() => {
                if (this.zippyCloseOnEvent) {
                    this.subscriptions.add(
                        this.events.on(this.zippyCloseOnEvent).subscribe(() => {
                            this.setVisibility(false);
                            this.reset();
                        })
                    );
                }
            });
        }

        if (!this.zippyMulti) {
            this.setVisibility(this._zippyState);
        }

        this.reset();
    }

    /**
     * On destroy hook
     * additinally triggers reset in case we need to open another zippy when this one is destroyed
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.reset();
    }

    protected onRouterEvent(): void {
        this.setVisibility(false);
        this.reset();
        this.clearStateLockTimeout();
        // we lock it for 1s not to be re-opend by hovering mouse or smt, we will release the lock after a moment
        const timeoutId = (this.stateLockedTimeout = this.timeoutService.setTimeout(() => {
            this.clearStateLockTimeout(timeoutId);
        }, 500));
    }

    /**
     * clears temp lock
     * @param timeoutId If given the reset will only be applied if id matches
     */
    private clearStateLockTimeout(timeoutId?: number) {
        if (this.stateLockedTimeout) {
            if (!timeoutId || timeoutId === this.stateLockedTimeout) {
                clearTimeout(this.stateLockedTimeout);
                this.stateLockedTimeout = undefined;
            }
        }
    }

    /**
     * Zippy events listener
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {IZippyEvent} event
     */
    protected onEvent(event: IZippyEvent) {
        if (!event.stopped) {
            switch (event.type) {
                case ZippyEventType.open:
                    if (this.checkEventMatchExact(event)) {
                        this.setVisibility(true);
                    } else if (!this.zippyMulti && this.checkEventMatchGroup(event)) {
                        this.setVisibility(false);
                    }
                    break;
                case ZippyEventType.close:
                    if (event.id.isGroup ? this.checkEventMatchGroup(event) : this.checkEventMatchExact(event)) {
                        this.setVisibility(false);
                    }
                    break;
                case ZippyEventType.toggle:
                    if (this.checkEventMatchExact(event)) {
                        event.type = this.isVisible() ? ZippyEventType.close : ZippyEventType.open;
                        this.subject.next(event);
                    }
                    break;
                // when event asks if item is open mark it setting stopped flag
                // we skip one that asked for it!
                case ZippyEventType.isOpen:
                    if (this.checkEventMatchExact(event) && this.isVisible()) {
                        event.stopped = true;
                    }
                    break;
                // when event asks if any is open mark it setting stopped flag
                // we skip one that asked for it!
                case ZippyEventType.isAnyOpen:
                    if (this.checkEventMatchGroup(event) && this.isVisible()) {
                        event.stopped = true;
                    }
                    break;
                // if asking for any to open we let first one stop propagation and open
                // only for zippyAuto!
                case ZippyEventType.openAny:
                    if (this.zippyAuto && this.checkEventMatchGroup(event)) {
                        event.stopped = true;
                        this.subject.next({
                            id: this.id,
                            type: ZippyEventType.open
                        });
                    }
                    break;
            }
        }
    }

    /**
     * Called when zippy.toggle even is fired
     *
     * Toggles state of matching zippy.
     * Triggers event to close other group elements
     *
     * @see zippy-toggle.directive
     * @param {event} $event
     * @param {string} zippyName Name of zippy requested to toggle
     * @param to
     */
    protected setVisibility(to: boolean) {
        if (this._zippyOff) {
            // set to open always if zippyOff flag is set
            to = true;
        }

        if (this.stateLockedTimeout) {
            to = this._zippyState;
        }

        this._zippyState = to;

        if (to !== this.isVisible()) {
            !to
                ? this.renderer2.addClass(this.ngEl.nativeElement, HIDDEN_CLASS)
                : this.renderer2.removeClass(this.ngEl.nativeElement, HIDDEN_CLASS);

            this.subject.next({
                id: this.id,
                type: ZippyEventType.change,
                visible: to
            });
        }
    }

    /**
     * Checks if zippy is visible
     */
    protected isVisible(): boolean {
        return !this.ngEl.nativeElement.classList.contains(HIDDEN_CLASS);
    }

    /**
     * Usually called on zippy destroy
     * if there is
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    protected reset() {
        // if none open then ask to open any
        if (!this.isAnyOpen()) {
            this.subject.next({
                id: this.id,
                type: ZippyEventType.openAny
            });
        }
    }

    protected detectChanges(): void {
        this.timeoutService.async(() => {
            if (!(<ViewRef>this.changeDetectorRef).destroyed) {
                this.changeDetectorRef.detectChanges();
            }
        });
    }
}
